// When each line runs: network.json's `hours`, from the timetable, so the
// page can say why a line has no trains. A line whose timetable has nothing
// running now isn't running now (the Tube at 3 a.m.); one that should have
// trains and has none has no live data.
//
// By service day ("20260926", the GTFS date), each line's spells of service
// as minutes from the day's start (noon less 12 hours: midnight but on the
// days the clocks change), start and end in turn: [300, 1470] is 05:00 to
// 00:30 the next morning. A spell is from a trip's first call to its last;
// trips less than MERGE_MIN apart are one spell. The build covers a few days
// around when it ran; the page knows nothing outside them.

import { eachRow } from './csv.ts';
import { serviceDayStart } from './schedule.ts';

export type GtfsHours = Readonly<Record<string, Readonly<Record<string, readonly number[]>>>>;

// Spells this close are one: a line with a train every 20 minutes runs.
const MERGE_MIN = 20;

// "25:10:00" as s after the service day's start; NaN when it isn't one.
export function gtfsSeconds(time: string): number {
  const m = /^\s*(\d+):(\d\d):(\d\d)\s*$/u.exec(time);
  return m ? Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]) : Number.NaN;
}

// One formatter per time zone: building one costs about 30 µs, and the page
// asks for a line's service days for every line on every refresh.
const dayFormats = new Map<string, Intl.DateTimeFormat>();

// "20260926" for an instant (ms), in `timeZone`, or UTC.
export function dayOf(ms: number, timeZone = 'UTC'): string {
  let format = dayFormats.get(timeZone);
  if (!format) {
    format = new Intl.DateTimeFormat('en-CA', { timeZone });
    dayFormats.set(timeZone, format);
  }
  return format.format(new Date(ms)).replaceAll('-', '');
}

// The calendar day `n` days from a "YYYYMMDD": days, not 24 hours (a day
// the clocks change has 23 or 25).
export function addDays(day: string, n: number): string {
  const date = new Date(Date.UTC(Number(day.slice(0, 4)), Number(day.slice(4, 6)) - 1, Number(day.slice(6, 8)) + n));
  return date.toISOString().slice(0, 10).replaceAll('-', '');
}

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
const weekdayOf = (day: string): (typeof WEEKDAYS)[number] =>
  WEEKDAYS[
    new Date(Date.UTC(Number(day.slice(0, 4)), Number(day.slice(4, 6)) - 1, Number(day.slice(6, 8)))).getUTCDay()
  ] ?? 'sunday';

// `files`: a timetable's by name, as text (network.ts GtfsFiles, not
// imported: the page reads this file and knows nothing of the build).

// Each day's services: calendar.txt's weeks with calendar_dates.txt's
// additions and removals.
function servicesByDay(files: ReadonlyMap<string, string>, days: readonly string[]): Map<string, Set<string>> {
  const byDay = new Map(days.map((d) => [d, new Set<string>()]));
  eachRow(files.get('calendar.txt') ?? '', (get) => {
    for (const [day, set] of byDay) {
      if (get('start_date') <= day && day <= get('end_date') && get(weekdayOf(day)) === '1') set.add(get('service_id'));
    }
  });
  eachRow(files.get('calendar_dates.txt') ?? '', (get) => {
    const set = byDay.get(get('date'));
    if (!set) return;
    if (get('exception_type') === '1') set.add(get('service_id'));
    else if (get('exception_type') === '2') set.delete(get('service_id'));
  });
  return byDay;
}

// A trip of the build: its line, service and first and last call (s).
export interface HoursTrip {
  readonly line: string;
  readonly service: string;
  readonly start: number;
  readonly end: number;
}

// The lines' spells on each of `days`. Empty without a calendar.
export function buildHours(
  files: ReadonlyMap<string, string>,
  trips: readonly HoursTrip[],
  days: readonly string[],
): Record<string, Record<string, number[]>> {
  if (!files.has('calendar.txt') && !files.has('calendar_dates.txt')) return {};
  const byDay = servicesByDay(files, days);
  const spells = new Map<string, Map<string, (readonly [number, number])[]>>();
  for (const t of trips) {
    for (const [day, services] of byDay) {
      if (!services.has(t.service)) continue;
      let lines = spells.get(day);
      if (!lines) {
        lines = new Map<string, (readonly [number, number])[]>();
        spells.set(day, lines);
      }
      let list = lines.get(t.line);
      if (!list) lines.set(t.line, (list = []));
      list.push([Math.floor(t.start / 60), Math.ceil(t.end / 60)]);
    }
  }
  const out: Record<string, Record<string, number[]>> = {};
  for (const day of days) {
    const lines = spells.get(day);
    const today: Record<string, number[]> = {};
    for (const line of [...(lines?.keys() ?? [])].toSorted()) {
      const list = lines?.get(line) ?? [];
      const merged: number[] = [];
      for (const [a, b] of list.toSorted((x: readonly [number, number], y: readonly [number, number]) => x[0] - y[0])) {
        const end = merged.at(-1);
        if (end !== undefined && a - end <= MERGE_MIN) merged[merged.length - 1] = Math.max(end, b);
        else merged.push(a, b);
      }
      today[line] = merged;
    }
    out[day] = today;
  }
  return out;
}

// The days a build made at `built` (ms) covers: the day before to two days
// after, in UTC, which takes in the service day that's today anywhere.
export function hoursDays(built: number): string[] {
  return [-1, 0, 1, 2].map((d) => dayOf(built + d * 86_400_000));
}

// ---- the page

// A spell counts from a few minutes before its first train.
const SLACK_MIN = 5;

export interface LineHours {
  // The timetable has the line running now.
  readonly running: boolean;
  // When it next starts, ms, when it isn't running and starts today or
  // tomorrow.
  readonly next: number | null;
}

// Whether the timetable has `line` running at `nowMs`, and if not, when it
// next does; null when the hours don't cover now (an old build, or none).
// Spells are placed from each service day's start as instants, so the days
// the clocks change come out right.
export function lineHours(
  hours: GtfsHours | undefined,
  line: string,
  nowMs: number,
  timeZone: string,
): LineHours | null {
  if (!hours) return null;
  const day = dayOf(nowMs, timeZone);
  if (!hours[day]) return null;
  // Yesterday's spells that run past midnight, today's and tomorrow's.
  const spells: (readonly [number, number])[] = [];
  for (const n of [-1, 0, 1]) {
    const d = addDays(day, n);
    const list = hours[d]?.[line] ?? [];
    const start = serviceDayStart(timeZone, Number(d.slice(0, 4)), Number(d.slice(4, 6)), Number(d.slice(6, 8))) * 1000;
    for (let i = 0; i + 1 < list.length; i += 2)
      spells.push([start + (list[i] ?? 0) * 60_000, start + (list[i + 1] ?? 0) * 60_000]);
  }
  if (spells.some(([a, b]) => a - SLACK_MIN * 60_000 <= nowMs && nowMs <= b)) return { running: true, next: null };
  const next = spells
    .map(([a]) => a)
    .filter((a) => a > nowMs)
    .toSorted((a, b) => a - b)[0];
  return { running: false, next: next ?? null };
}
